import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import InfoTip from "./InfoTip";
import {
  DEFAULT_YETI_GOAL, normalizeYetiGoal, normalizeYetiGoals, yetiByTeamOn,
} from "../../../helpers/beatYeti";
import "./customize.css";

/**
 * What a Beat the Yeti rate card adds around its big percentage — independent
 * switches, shown only when Beat the Yeti rate is one of the card's metrics (a
 * card on any other metric never sees controls that would do nothing):
 *
 *   Count by team   a "Technicians 7/10 · 70%" row for every workspace the card
 *                   tracks — no separate list to keep in step, so the rows
 *                   always add up to the big number
 *   Progress bar    the overall rate as a bar under the big number
 *   Goal notch      a marker on those bars at a chosen share of players beating
 *                   the Yeti: green once reached, red while not. One overall
 *                   goal, and — because Technicians and Sales do not aim at the
 *                   same share — an optional goal per tracked workspace.
 *
 * `value` is the card's settings; `workspaces` is [{ id, name }] for the
 * workspaces the card tracks; `onChange` gets only the keys that changed.
 */
const Toggle = ({ on, title, sub, onClick }) => (
  <button type="button" className={`gmbw-whotog${on ? " on" : ""}`} onClick={onClick} aria-pressed={on}>
    <span className="gmbw-whotog-t">
      <b>{title}</b>
      <span>{sub}</span>
    </span>
    <span className="gmbw-whotog-sw"><i /></span>
  </button>
);
Toggle.propTypes = {
  on: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  sub: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

/* A goal box is typed into, so it keeps its own text: clearing "50" to type
   "65" must not read as "goal switched off" halfway through. A valid number is
   committed as it is typed; `allowEmpty` (a team's own goal) also commits a
   cleared box, meaning "use the overall goal". */
const GoalInput = ({ value, placeholder, allowEmpty, label, onCommit }) => {
  const [text, setText] = useState(value ? String(value) : "");
  useEffect(() => { setText(value ? String(value) : ""); }, [value]);
  return (
    <input
      className="gmbw-input"
      style={{ width: 64, flex: "none", textAlign: "center" }}
      type="number"
      min="1"
      max="100"
      step="1"
      inputMode="numeric"
      placeholder={placeholder}
      aria-label={label}
      value={text}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        if (raw === "" && allowEmpty) { onCommit(null); return; }
        const g = normalizeYetiGoal(raw);
        if (g) onCommit(g);
      }}
      onBlur={() => setText(value ? String(value) : "")}
    />
  );
};
GoalInput.propTypes = {
  value: PropTypes.number,
  placeholder: PropTypes.string,
  allowEmpty: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onCommit: PropTypes.func.isRequired,
};
GoalInput.defaultProps = { value: null, placeholder: "", allowEmpty: false };

const YetiOptionsField = ({ value, workspaces, onChange }) => {
  const byTeam = yetiByTeamOn(value);
  const bar = value.yetiBar === true;
  const goal = normalizeYetiGoal(value.yetiGoal);
  const goals = normalizeYetiGoals(value.yetiGoals);

  const setTeamGoal = (id, g) => {
    const next = { ...goals };
    if (g) next[String(id)] = g;
    else delete next[String(id)];
    onChange({ yetiGoals: next });
  };

  return (
    <div className="gmbw-fld">
      <label>
        Beat the Yeti display
        <InfoTip text="Extras around the big percentage. The team rows follow the workspaces this card tracks, so they always add up to it. Only players with a score in the date range are counted, each against their own team's Yeti." />
      </label>
      <Toggle
        on={byTeam}
        title="Show the count by team"
        sub="A row per tracked workspace, like Technicians 7/10."
        /* Clear the old hand-picked list too, or it would keep the switch on. */
        onClick={() => onChange({ yetiByTeam: !byTeam, yetiTeams: [] })}
      />
      <Toggle
        on={bar}
        title="Show a progress bar"
        sub="The overall rate as a bar under the big percentage."
        onClick={() => onChange({ yetiBar: !bar })}
      />
      <Toggle
        on={!!goal}
        title="Show a goal notch"
        sub="A marker on the bars. Green when reached, red when not."
        onClick={() => onChange({ yetiGoal: goal ? null : DEFAULT_YETI_GOAL })}
      />
      {goal ? (
        <div className="gmbw-yetigoals">
          <p className="gmbw-hint" style={{ margin: "0 0 8px" }}>
            A goal is the share of players who should be beating the Yeti.
          </p>
          <div className="gmbw-yetigoals-row">
            <span className="gmbw-yetigoals-n">{byTeam ? "Overall" : "Goal"}</span>
            <GoalInput
              value={goal}
              label="Overall goal, percent of players beating the Yeti"
              onCommit={(g) => onChange({ yetiGoal: g })}
            />
            <span className="gmbw-yetigoals-u">%</span>
          </div>
          {byTeam && workspaces.length ? (
            <>
              <p className="gmbw-hint" style={{ margin: "10px 0 6px" }}>
                Teams don&apos;t all aim at the same share. Set a goal for each one, or leave it blank to use the overall goal.
              </p>
              {workspaces.map((w) => (
                <div className="gmbw-yetigoals-row" key={w.id}>
                  <span className="gmbw-yetigoals-n" title={w.name}>{w.name}</span>
                  <GoalInput
                    value={goals[String(w.id)] || null}
                    placeholder={String(goal)}
                    allowEmpty
                    label={`${w.name} goal, percent of players beating the Yeti`}
                    onCommit={(g) => setTeamGoal(w.id, g)}
                  />
                  <span className="gmbw-yetigoals-u">%</span>
                </div>
              ))}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

YetiOptionsField.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.object,
  workspaces: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  onChange: PropTypes.func.isRequired,
};
YetiOptionsField.defaultProps = { value: {}, workspaces: [] };

export default YetiOptionsField;
